import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Minus, Plus } from "lucide-react";
import { Seo } from "../components/Seo";
import { CtaBand } from "../components/home/CtaBand";
import { Eyebrow, Reveal } from "../components/motion";
import { PROGRAM, WEBINAR } from "../data/content";

const Step = ({ s, open, onToggle }) => (
  <div className="border-t border-white/10" data-testid={`formation-step-${s.n}`}>
    <button onClick={onToggle} aria-expanded={open} data-testid={`formation-step-toggle-${s.n}`} className="group flex w-full items-start gap-6 py-7 text-left">
      <span className="font-mono text-xs tracking-[0.3em] text-neon">{s.n}</span>
      <span className="flex-1">
        <span className="block font-serif text-2xl leading-tight text-cream transition-colors group-hover:text-neon-light sm:text-3xl">{s.title}</span>
        <span className="mt-2 block text-sm text-white/50">{s.lead}</span>
      </span>
      <span className="grid h-9 w-9 shrink-0 place-items-center border border-white/15 text-cream transition-colors group-hover:border-neon group-hover:text-neon">
        {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </span>
    </button>
    {open && (
      <div className="grid gap-6 pb-8 pl-12 sm:pl-16 lg:grid-cols-12">
        <ul className="space-y-3 lg:col-span-8">
          {s.points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-white/65">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" /> {p}
            </li>
          ))}
        </ul>
        <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-neon-light lg:col-span-4 lg:text-right">Je le fais avec toi — {s.with}</p>
      </div>
    )}
  </div>
);

export default function Formation() {
  const navigate = useNavigate();
  const [open, setOpen] = useState("01");
  const [faq, setFaq] = useState(null);
  return (
    <main data-testid="page-formation">
      <Seo
        title="Formation & accompagnement — le programme en 10 étapes | Demoiselle Atypique"
        description="Stratégie, recherche du bien, validation technique, concept, négociation, financement, structure juridique, travaux, lancement, marque : le programme complet d'accompagnement Demoiselle Atypique pour créer un logement atypique rentable."
      />

      <section className="px-6 pb-16 pt-40 sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-[1500px] gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Eyebrow>{PROGRAM.eyebrow}</Eyebrow>
            <h1 className="mt-6 font-serif text-5xl leading-[1.06] text-cream sm:text-6xl lg:text-7xl" data-testid="formation-title">{PROGRAM.title}</h1>
          </div>
          <p className="leading-relaxed text-white/60 lg:col-span-4">{PROGRAM.intro}</p>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#1D1327] py-20 lg:py-28" data-testid="formation-pour-qui">
        <div className="mx-auto grid max-w-[1500px] gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-4">
            <Reveal><Eyebrow>Pour qui</Eyebrow></Reveal>
            <Reveal delay={0.08}><h2 className="mt-6 font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">Aucun de nos clients n'était investisseur avant de se lancer.</h2></Reveal>
          </div>
          <ul className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
            {PROGRAM.forWho.map((f, i) => (
              <Reveal key={f} delay={0.1 + i * 0.06}>
                <li className="flex h-full items-start gap-3 border border-white/10 bg-white/[0.02] p-5 text-sm leading-relaxed text-white/70">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" /> {f}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="formation-programme">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>Le programme</Eyebrow></Reveal>
          <Reveal delay={0.08}><h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">Dix étapes, dans l'ordre. Tu n'es jamais seul(e).</h2></Reveal>
          <div className="mt-14 border-b border-white/10">
            {PROGRAM.steps.map((s) => (
              <Step key={s.n} s={s} open={open === s.n} onToggle={() => setOpen(open === s.n ? null : s.n)} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#1D1327] py-24 lg:py-32" data-testid="formation-faq">
        <div className="mx-auto grid max-w-[1500px] gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-4">
            <Reveal><Eyebrow>La banque & le financement</Eyebrow></Reveal>
            <Reveal delay={0.08}><h2 className="mt-6 font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">Les questions qu'on nous pose toujours.</h2></Reveal>
          </div>
          <div className="lg:col-span-8">
            {PROGRAM.faq.map((f, i) => (
              <div key={f.q} className="border-t border-white/10 last:border-b" data-testid={`formation-faq-${i}`}>
                <button onClick={() => setFaq(faq === i ? null : i)} aria-expanded={faq === i} className="flex w-full items-center justify-between gap-6 py-6 text-left font-serif text-2xl text-cream hover:text-neon-light">
                  {f.q}
                  {faq === i ? <Minus className="h-4 w-4 shrink-0 text-neon" /> : <Plus className="h-4 w-4 shrink-0 text-neon" />}
                </button>
                {faq === i && <p className="pb-7 leading-relaxed text-white/60">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="formation-bilan">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
          <div className="grid gap-10 border border-neon/30 bg-neon/[0.04] p-8 lg:grid-cols-12 lg:p-14">
            <div className="lg:col-span-7">
              <Eyebrow>{PROGRAM.bilan.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">{PROGRAM.bilan.title}</h2>
              <ul className="mt-8 space-y-3">
                {PROGRAM.bilan.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-white/70"><Check className="mt-1 h-4 w-4 shrink-0 text-neon" /> {p}</li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col justify-end gap-4 lg:col-span-5 lg:items-end">
              <button onClick={() => navigate("/rendez-vous")} data-testid="formation-cta-bilan" className="btn-primary w-full justify-center sm:w-auto">
                {PROGRAM.bilan.cta} <ArrowRight className="h-4 w-4" />
              </button>
              {WEBINAR.enabled && (
                <Link to="/" state={{ scrollTo: "webinaire" }} data-testid="formation-cta-webinaire" className="btn-outline w-full justify-center sm:w-auto">
                  Découvrir le webinaire gratuit <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
